/**
 * "Message on Slack" — deep-links a Business Partner into a real Slack DM with a ticket's
 * submitter, in the BP's own identity. Slack has no URL parameter to pre-fill a DM's compose box
 * (unlike e.g. `mailto:body=`), so the drafted message is copied to the clipboard instead; the BP
 * pastes and sends it themselves once the DM opens. See TicketDetailModal.
 *
 * The draft deliberately reads as a template, not a faked-human note (David's call): a banner
 * line names it as a follow-up on a specific ticket, then a plain greeting — the BP writes the
 * actual message themselves underneath.
 */
import type { BpTicket } from '../../types/bpTickets';

/** "jana.kowalski@finn.com" → "Jana" (same split-on-dot convention as the proxy's fullNameFromEmail). */
function firstNameFromEmail(email: string): string | null {
  const local = `${email}`.split('@')[0]?.trim();
  if (!local) return null;
  const [first] = local.split('.');
  const name = first || local;
  return name.charAt(0).toUpperCase() + name.slice(1);
}

export function buildTicketOutreachMessage(
  ticket: Pick<BpTicket, 'ticketNumber' | 'summary' | 'submittedBy'>,
): string {
  const ref = ticket.ticketNumber != null ? ` #${ticket.ticketNumber}` : '';
  const title = ticket.summary ? ` - ${ticket.summary}` : '';
  const firstName = firstNameFromEmail(ticket.submittedBy);
  const greeting = firstName ? `Hey ${firstName},` : 'Hey,';
  return `+++ Follow-Up on your ticket${ref}${title} +++\n\n${greeting}\n`;
}

export function slackDmUrl(slackUserId: string): string {
  return `https://slack.com/app_redirect?channel=${encodeURIComponent(slackUserId)}`;
}
